import { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import get from "lodash/get";
import { XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { REQUIRED_MARK } from "components/BaseForm/fields/fieldClasses";
import { PRICE_BASIS } from "routes/product/pricing/pricing";

const termError = (draft, terms, priceBasis) => {
  const value = draft.trim();
  if (!/^\d+$/.test(value) || parseInt(value, 10) < 1) {
    return "Enter a whole number of years, 1 or greater.";
  }
  if (terms.includes(parseInt(value, 10))) {
    return "This term is already allowed on this product.";
  }
  if (priceBasis === PRICE_BASIS.WHOLE_TERM && terms.length >= 1) {
    return "Products priced for the whole term can only have 1 allowed term.";
  }
  return null;
};

const AllowedTermsField = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const priceBasis = useWatch({ name: "price_basis" });
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState(null);
  const listError = get(errors, "allowed_terms")?.message;

  return (
    <Controller
      name="allowed_terms"
      control={control}
      render={({ field }) => {
        const terms = field.value ?? [];
        const addTerm = () => {
          const message = termError(draft, terms, priceBasis);
          setDraftError(message);
          if (message) return;
          field.onChange(
            [...terms, parseInt(draft.trim(), 10)].sort((a, b) => a - b)
          );
          setDraft("");
        };
        return (
          <Field data-invalid={listError || draftError ? true : undefined}>
            <FieldLabel htmlFor="new-term" className={REQUIRED_MARK}>
              Allowed Terms (years)
            </FieldLabel>
            {terms.length > 0 && (
              <div className="tw:flex tw:flex-wrap tw:gap-1">
                {terms.map(term => (
                  <Badge key={term} variant="secondary">
                    {term} {term === 1 ? "year" : "years"}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Remove ${term} year term`}
                      onClick={() =>
                        field.onChange(terms.filter(t => t !== term))
                      }
                    >
                      <XIcon data-icon="inline-end" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="tw:flex tw:items-center tw:gap-2">
              <Input
                id="new-term"
                className="tw:w-24"
                inputMode="numeric"
                value={draft}
                onChange={event => setDraft(event.target.value)}
                aria-invalid={draftError ? true : undefined}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTerm}
              >
                Add Term
              </Button>
            </div>
            <FieldError>{draftError ?? listError}</FieldError>
          </Field>
        );
      }}
    />
  );
};

export default AllowedTermsField;
